import Phaser from 'phaser';
import { createEngine, FairDice, type Engine } from './bridgeTypes.js';
import { previewWouldWin } from '@casino/craps-engine';
import { clampBetAmount } from './clampBetAmount.js';
import { pickLongPressTarget } from './pickLongPressTarget.js';
import * as feel from '../feel.js';
import { ChipStackVisual } from '../objects/ChipStackVisual.js';
import { flyWinningChips } from '../objects/winningsFly.js';
import { flyChipFromSource, flyChipToRack } from '../objects/chipFlyIn.js';
import { pointPos } from '../objects/pointPos.js';
import { flashRollLabel, rollLabel } from '../objects/rollFlash.js';
import type { CrapsSnapshot, TableState, TableRules } from '@casino/craps-engine';
import type { ZoneDef } from '../layout/zones.js';
import type { BetZone } from '../objects/BetZone.js';
import type { TableScene } from '../scenes/craps/TableScene.js';
import type { UIScene } from '../scenes/craps/UIScene.js';
import { resolveZoneHelp } from '../scenes/craps/zoneHelpCopy.js';
import { openBustModal } from '../components/BustModal.js';
import {
  load, save,
  getTableRules, getTablePrefs,
  checkUnlocks, setSelectedDenom,
  getActiveGame, startActiveGame, updateActiveSnapshot, endActiveGame,
} from '../persistence.js';
import type { SessionRecorder } from '../sessionRecorder.js';
import { computeForfeit } from './tableLeave.js';
import { showLeaveConfirmModal } from '../components/leaveConfirmModal.js';
import { randomUuid } from '../sessionStorage.js';
import { canResumeRecording } from '../recordingState.js';

/** Position threshold (cents) under which we pop the bust-recovery modal
 *  once per session. "Position" = bankroll + sum of active wagers, so a
 *  player with $0 bankroll and $1000 on the felt is NOT considered broke —
 *  they still have plenty in play. Modal fires only when both the rack and
 *  the table together fall under this number. */
const LOW_POSITION_THRESHOLD_CENTS = 20_000;
const BUST_MODAL_SESSION_KEY = 'casino.bust_modal_shown';
/** Beat between the dice physically coming to rest and the bust modal
 *  appearing. Anchored to the dice, not to the stickman call or the payout
 *  animations — those finish at variable times, which made the modal read as
 *  arriving at random (player feedback ids 22/23). */
const BUST_MODAL_SETTLE_DELAY_MS = 1000;

export interface BridgeOptions {
  table: TableScene;
  ui: UIScene;
  initialBankrollCents?: number;
  /** Persisted-table key. Defaults to 'standard' for back-compat. */
  tableKey?: 'standard' | 'high-limit' | 'whale';
  /** Recorder for session replay. Optional during phased rollout — when
   *  absent (or in spectate mode), nothing is recorded. */
  recorder?: SessionRecorder;
  /** Default 'play'. In 'spectate', inputs/persistence/bust-modal disabled
   *  and the engine is driven by re-deriving state from the input log. */
  mode?: 'play' | 'spectate';
  /** Real analytics device id, threaded into the recorded session. */
  deviceId?: string;
}

/** Version of the turn-structured replay log this bridge writes. Bump when
 *  the on-disk event/keyframe shape changes incompatibly. */
const REPLAY_SCHEMA_VERSION = 3;

export function createCrapsBridge(opts: BridgeOptions): Engine {
  const { table, ui, tableKey = 'standard' } = opts;
  const mode = opts.mode ?? 'play';
  const persisted = load();
  const rules = getTableRules<TableRules>(persisted, 'craps', tableKey);
  const prefs = getTablePrefs(persisted, 'craps', tableKey);
  const resuming = mode === 'play' && getActiveGame(persisted)?.tableId === `craps:${tableKey}`;
  const resumeSnap = resuming
    ? (getActiveGame(persisted)!.snapshot as CrapsSnapshot)
    : null;
  const initialBankroll = resumeSnap?.bankroll ?? persisted.bankrollCents;
  const engine = createEngine({
    dice: pickDice(),
    rules,
    bankroll: initialBankroll,
  });

  // Phaser scene event emitters survive scene shutdown — bare `on(...)`
  // calls would stack up across navigations (e.g. lobby → standard →
  // lobby → high-limit), leaving stale bridges driving the same scene
  // and causing visible cross-talk like multiple engines resolving the
  // same dice roll. Track every subscription here and drop them all on
  // SHUTDOWN so each visit starts fresh.
  const disposers: Array<() => void> = [];
  // Rebuild the chip rack so denominations below table-min (e.g. $1 chips on
  // a $5 minimum table) don't appear in the rack at all.
  opts.table.refreshChipRack(rules.tableMin, rules.tableMax);
  opts.table.rack.selectedCents = prefs.selectedDenomCents;

  // Restore the previous session (bets on the layout, current point/phase,
  // history) so refreshing or leaving/re-entering doesn't forfeit chips.
  //
  // Spectate mode skips this entirely — the spectator's own localStorage is
  // irrelevant; their engine should mirror the *producer's* state. If we
  // hydrated from the spectator's own session here, then producer's hydrate
  // arrived with FEWER bets (or none), no BetPlaced events would fire on
  // the producer's hydrate, and the spectator's chips would stay painted.
  //
  // Strip bankroll from the snapshot before hydrating. For the player, the
  // v2 root's `bankrollCents` is the authoritative source — it survives
  // reset() and tracks the live bankroll across all tables. A stale session
  // snapshot's bankroll (e.g. from a previous bust) would otherwise clobber
  // the lobby's current value. Spectator catchup uses a different code path
  // that DOES need snapshot.bankroll, so the engine's hydrate keeps that
  // behavior.
  if (resumeSnap) {
    // Resume the in-flight game. Engine bankroll was already seeded from the
    // snapshot above; hydrate restores bets/phase/history (it does not touch
    // bankroll by contract).
    const { bankroll: _drop, ...snapNoBankroll } = resumeSnap;
    engine.hydrate(snapNoBankroll as CrapsSnapshot);
  }

  // Drive the session recorder if one was provided. The recorder records the
  // game as shooter-turns: startGame/resumeGame opens a turn (with a keyframe),
  // every engine input is forwarded as a turn-tagged event, and a seven-out
  // opens the next turn (see the boundary handling below).
  // Spectate mode never records — the spectator's engine is driven by
  // external events, and re-broadcasting them would create a feedback loop.
  // Resume the turn log only if the persisted pointer carries a valid turn-model
  // identity; a legacy/cross-deploy pointer (felt resumes, but no gameId) starts a
  // fresh game so the durable log gets a clean keyframe instead of undefined ids.
  const resumeRec = resuming && canResumeRecording(getActiveGame(persisted));
  const mintedGameId = resumeRec ? getActiveGame(persisted)!.gameId : randomUuid();
  // Set on the SevenOut VIEW event; consumed by the input handler so the
  // boundary fires AFTER the seven-out roll is recorded (see step 3 below).
  let sevenOutPending = false;
  if (mode === 'play' && opts.recorder) {
    // Let the recorder read the live felt so toggling Broadcast on mid-game can
    // bootstrap the relay with the current state (not just from the next turn).
    opts.recorder.setKeyframeSource(() => engine.serialize());
    if (resumeRec) {
      const resumed = getActiveGame(persisted)!;
      opts.recorder.resumeGame({
        gameId: resumed.gameId, deviceId: opts.deviceId ?? '', game: 'craps', tableKey,
        rules, replaySchemaVersion: REPLAY_SCHEMA_VERSION,
        turnId: resumed.turnId, lastSeq: resumed.lastSeq,
        keyframe: engine.serialize() as never,
      });
    } else {
      opts.recorder.startGame({
        gameId: mintedGameId, deviceId: opts.deviceId ?? '', game: 'craps', tableKey,
        rules, replaySchemaVersion: REPLAY_SCHEMA_VERSION,
        keyframe: engine.serialize() as never, bankrollStart: initialBankroll,
      });
    }
    // Forward each engine input as a turn-tagged event. On the seven-out roll we
    // must record the roll FIRST, then open the next turn — so we defer the
    // boundary (set by the SevenOut view event below) until after recordInput.
    disposers.push(engine.subscribeInputs((i) => {
      opts.recorder!.recordInput(i);
      if (sevenOutPending) {
        sevenOutPending = false;
        const snap = engine.serialize();
        opts.recorder!.boundary({ keyframe: snap as never, bankrollStart: engine.getState().bankroll });
      }
    }));
  }

  // Persist the active-game pointer whenever we started a fresh recording (clean
  // entry OR a legacy felt-resume that minted a new gameId). On a real resume the
  // pointer already carries the right identity — leave it.
  if (mode === 'play' && !resumeRec) {
    save(startActiveGame(load(), {
      tableId: `craps:${tableKey}`,
      gameId: mintedGameId, turnId: 1, lastSeq: 0,
      startedAt: Date.now(),
      snapshot: engine.serialize(),
    }));
  }

  // Snapshot of each ATS bet's seen-set BEFORE the upcoming roll resolves.
  // refreshAtsBubbles reads this to detect which bubbles got newly lit on
  // the current roll (so it can pulse the matching bubble for emphasis), and
  // to distinguish "zone lost its bet on a 7" from "zone never had a bet".
  let prevSeenByBetId = new Map<string, Set<number>>();
  let prevAtsTypesWithBet = new Set<'small' | 'tall' | 'all'>();
  const captureAtsSeen = () => {
    prevSeenByBetId = new Map();
    prevAtsTypesWithBet = new Set();
    for (const b of engine.getState().bets) {
      if (b.type !== 'small' && b.type !== 'tall' && b.type !== 'all') continue;
      const seen = (b.metadata?.seen as number[] | undefined) ?? [];
      prevSeenByBetId.set(b.id, new Set(seen));
      prevAtsTypesWithBet.add(b.type);
    }
  };

  const refreshAtsBubbles = (rollTotal: number) => {
    // Walk current ATS bets (post-resolution — winning/losing bets removed).
    const remaining = new Set<'small' | 'tall' | 'all'>();
    for (const b of engine.getState().bets) {
      if (b.type !== 'small' && b.type !== 'tall' && b.type !== 'all') continue;
      remaining.add(b.type);
      const seen = (b.metadata?.seen as number[] | undefined) ?? [];
      const zone = table.atsZones.find((z) => z.def.type === b.type);
      if (!zone) continue;
      zone.setLitNumbers(seen);
      zone.setBetActive(true);
      zone.setChipStackCents(b.amount);
      // Pulse newly-lit bubbles. "Newly lit" = in current seen but not in
      // the pre-roll snapshot for THIS bet.
      const prev = prevSeenByBetId.get(b.id) ?? new Set<number>();
      for (const n of seen) {
        if (!prev.has(n)) zone.pulseNumber(n, 'gold');
      }
    }
    // ATS zone types that HAD a bet pre-roll but no longer have one = just
    // resolved (won or lost). Reset their visual state. If the resolution
    // was a 7-out specifically, flash red across every required-number
    // bubble for dramatic loss feedback.
    for (const zone of table.atsZones) {
      const t = zone.def.type as 'small' | 'tall' | 'all';
      if (t !== 'small' && t !== 'tall' && t !== 'all') continue;
      if (remaining.has(t)) continue;
      zone.setLitNumbers([]);
      zone.setBetActive(false);
      zone.setChipStackCents(0);
      if (rollTotal === 7 && prevAtsTypesWithBet.has(t)) {
        const required = t === 'small' ? [2, 3, 4, 5, 6]
          : t === 'tall' ? [8, 9, 10, 11, 12]
          : [2, 3, 4, 5, 6, 8, 9, 10, 11, 12];
        for (const n of required) zone.pulseNumber(n, 'red');
      }
    }
  };

  // Wire a live engine-state getter into the UI so the tooltip resolver can
  // pick odds vs. line copy based on current phase/bets.
  ui.bindEngineState(() => engine.getState());

  // Initial state push — also covers hydrated-bet visuals since hydrate()
  // runs before subscribers attach (its replayed events are dropped, so we
  // refresh the chip stacks + puck explicitly here).
  ui.setBankrollCents(engine.getState().bankroll);
  table.rack.setBankrollCents(engine.getState().bankroll);
  ui.setHistory(engine.getState().history);
  refreshChipStacks(engine, table);
  // Initial render of any persisted ATS bets — restore lit bubbles to match
  // metadata.seen. No animations on hydration (only the static state).
  refreshAtsBubbles(0);
  refreshLegality(engine, table);
  refreshRollGate(engine, table);
  if (engine.getState().phase === 'POINT' && engine.getState().point !== undefined) {
    const target = pointPos(engine.getState().point!, table.zones);
    if (target) table.puck.setOn(engine.getState().point!, target.x, target.y);
  }

  // Persist the engine session whenever it changes meaningfully (a bet
  // placed/removed, a roll resolved, a phase transition).
  // Spectate mode never persists — its engine mirrors someone else's state.
  if (mode === 'play') {
    const persistSession = () => {
      // Persist the full engine snapshot INCLUDING bankroll — for the single
      // active game, snapshot.bankroll is the live cash-in-hand the resume
      // path reads back. The floor wallet (bankrollCents) stays frozen until
      // the session ends.
      save(updateActiveSnapshot(load(), engine.serialize()));
    };
    disposers.push(engine.subscribe((event) => {
      switch (event.type) {
        case 'BetPlaced':
        case 'BetRemoved':
        case 'BetWorkingChanged':
        case 'PointEstablished':
        case 'PointMade':
        case 'RollResolved':
          persistSession();
          break;
        case 'SevenOut':
          persistSession();
          // Defer the recorder turn boundary to the input handler so the
          // seven-out `roll` input is recorded under the ENDING turn first;
          // the boundary then opens the next turn. The SevenOut VIEW event
          // fires before the `roll` INPUT event within engine.roll(), so the
          // input handler will see this flag set.
          sevenOutPending = true;
          break;
      }
    }));
  }

  // While a roll is animating, defer all UI side-effects (bankroll change,
  // puck on/off, phase/point/legality) so the player only sees the outcome
  // after the dice finish tumbling.
  let rolling = false;
  // Set to true right before engine.removeBet so the next BankrollChanged
  // (the refund) snaps the bankroll without firing the win-pop animation.
  let suppressNextBankrollPop = false;
  const pending: Array<() => void> = [];
  const apply = (fn: () => void) => {
    if (rolling) pending.push(fn);
    else fn();
  };
  let stateAtRollStart: TableState | undefined;
  // Spectator-only state: tracks the auto-throw kicked off by
  // RollStarted's embedded result, plus whether the dice have come to
  // rest. RollResolved gates runSettleFeedback on the dice actually
  // settling so the stickman call + winning-zone flash land in sync
  // with the dice landing — not the instant the WS frame arrives.
  let spectatorAutoThrew = false;
  let spectatorDiceSettled = true;
  let spectatorPendingSettle: (() => void) | null = null;

  // Bust modal is anchored to the dice themselves: one beat after they come
  // to rest. Deliberately NOT chained off the stickman call or the payout
  // flies — those outlive the dice's own settle → hold → return-home cycle,
  // so the modal used to land in the middle of the NEXT throw.
  let pendingBustCheck: Phaser.Time.TimerEvent | undefined;
  const cancelBustCheck = () => {
    pendingBustCheck?.remove();
    pendingBustCheck = undefined;
  };
  const scheduleBustCheck = () => {
    if (mode !== 'play') return;
    cancelBustCheck();
    pendingBustCheck = ui.time.delayedCall(BUST_MODAL_SETTLE_DELAY_MS, () => {
      pendingBustCheck = undefined;
      // Belt-and-braces: if the player has already re-thrown, swallow this
      // check. The next settle schedules a fresh one.
      if (rolling) return;
      maybeShowBustModal(engine.getState());
    });
  };
  disposers.push(() => cancelBustCheck());

  // Engine → UI
  disposers.push(engine.subscribe((event) => {
    switch (event.type) {
      case 'BankrollChanged': {
        // Do NOT write the floor wallet here — while a game is active the live
        // bankroll lives in activeGame.snapshot (persisted via persistSession).
        // Still run the unlock check against the live bankroll so the
        // "tier unlocked" toast fires at the table; unlock flags are per-table
        // and independent of the wallet value.
        const unlocked = mode === 'play'
          ? (() => {
              const { next, unlocked } = checkUnlocks(load(), event.bankroll);
              save(next);
              return unlocked;
            })()
          : [];
        const animate = !suppressNextBankrollPop;
        suppressNextBankrollPop = false;
        apply(() => {
          ui.setBankrollCents(event.bankroll, { animate });
          table.rack.setBankrollCents(event.bankroll);
          const unlockDelay = animate ? 950 : 0;
          for (const tableId of unlocked) {
            if (unlockDelay > 0) {
              ui.time.delayedCall(unlockDelay, () => ui.showTierUnlockToast(tableId));
            } else {
              ui.showTierUnlockToast(tableId);
            }
          }
        });
        break;
      }
      case 'PhaseChanged':
        apply(() => refreshLegality(engine, table));
        // PhaseChanged fires on every hydrate (engine.hydrate emits it
        // unconditionally) — and hydrate intentionally NO LONGER emits
        // per-bet BetPlaced. So this is now the authoritative path for
        // re-painting restored bets after a hydrate. Cheap and idempotent
        // during normal phase transitions too.
        apply(() => refreshChipStacks(engine, table));
        apply(() => refreshAtsBubbles(0));
        apply(() => refreshRollGate(engine, table));
        apply(() => {
          // Sync puck position with current state. Fixes the case where a
          // spectator's hydrate drops state.point but PointMade/SevenOut
          // isn't replayed — without this the puck would freeze on the
          // pre-hydrate number.
          const st = engine.getState();
          if (st.phase === 'POINT' && st.point !== undefined) {
            const target = pointPos(st.point, table.zones);
            if (target) table.puck.setOn(st.point, target.x, target.y);
          } else {
            table.puck.setOff(table.puckOffAnchor.x, table.puckOffAnchor.y);
          }
        });
        break;
      case 'PointEstablished': {
        const target = pointPos(event.point, table.zones);
        if (target) apply(() => table.puck.setOn(event.point, target.x, target.y));
        break;
      }
      case 'PointMade':
      case 'SevenOut':
        // Slide the puck back to its OFF resting spot inside the don't-come
        // wedge — without coords setOff would just flip the texture and leave
        // the puck sitting on the just-resolved point number.
        apply(() => table.puck.setOff(table.puckOffAnchor.x, table.puckOffAnchor.y));
        break;
      case 'BetPlaced': {
        refreshRollGate(engine, table);
        const commitPlacement = () => {
          refreshChipStacks(engine, table);
          refreshAtsBubbles(0);
        };
        // Snap a transient chip from the rack into the destination zone,
        // then refresh the underlying stack so the new chip "arrives"
        // rather than popping in before the fly. refreshChipStacks /
        // refreshAtsBubbles rebuild from current engine state, so
        // deferring them by ~100ms only delays the just-placed chip's
        // appearance — no other zones depend on this call.
        //
        // Spectator note: the rack on the spectator's screen has no
        // semantic meaning relative to the producer's actual chip pick,
        // but visually originating the fly from the rack still reads
        // correctly as "a bet just landed there." This path runs for
        // both play AND spectate now — hydrate's per-bet BetPlaced emit
        // was removed, so we no longer get N concurrent fly animations
        // from a single hydrate replay.
        const dprFly = (table.game.registry.get('dpr') as number | undefined) ?? 1;
        const target = findZoneAnchorForBet(event.bet, engine.getState(), table);
        if (target) {
          flyChipFromSource({
            scene: ui,
            sourceAnchor: table.rack.getSelectedChipAnchor(),
            tableAnchor: target.anchor,
            cents: event.bet.amount,
            chipRadius: target.chipRadius,
            dpr: dprFly,
          }).then(commitPlacement);
        } else {
          commitPlacement();
        }
        break;
      }
      case 'BetRemoved':
        // Suppress the win-pop on the refund BankrollChanged that follows
        // this event on the wire (engine.removeBet emits BetRemoved BEFORE
        // setBankroll specifically so this flag can be set in time).
        // On the producer side this is already pre-set by the long-press
        // handler — setting it again here is a harmless redundancy. On
        // the spectator side this is THE place it gets set, because the
        // spectator doesn't run the long-press flow that originates the
        // removal.
        suppressNextBankrollPop = true;
        // Chip-fly back to the rack — mirrors what the producer's
        // onZoneLongPress / onTakeDownClick does. Runs for both modes:
        // on the producer the long-press handler also fires this fly,
        // so the spectator's BetRemoved emit would double-fly — but
        // producer's removeBet emits before its own bridge sees the
        // event, and the producer pre-fires the fly via the long-press
        // handler. We can avoid the double by guarding: if event.bet is
        // missing (older wire, or no bet captured), skip; if it IS
        // present, fly it. Producer's pre-fire happens regardless; the
        // duplicate fly will just look like a double-puff which is rare
        // in practice. For cleanliness we only fire here in spectate.
        if (mode === 'spectate' && event.bet) {
          const dprFly = (table.game.registry.get('dpr') as number | undefined) ?? 1;
          const target = findZoneAnchorForBet(event.bet, engine.getState(), table);
          if (target) {
            flyChipToRack({
              scene: ui,
              rackAnchor: table.rack.getChipAnchor(event.bet.amount),
              tableAnchor: target.anchor,
              cents: event.bet.amount,
              chipRadius: target.chipRadius,
              dpr: dprFly,
            });
          }
        }
        refreshChipStacks(engine, table);
        // refreshChipStacks only walks the standard table.zones map; ATS
        // zones live in their own collection and need a separate pass to
        // update chip stacks + lit state immediately. Without this the
        // chips stay invisible until the first roll triggers
        // refreshAtsBubbles via RollResolved. Pass rollTotal=0 so the
        // 7-out red flash branch stays gated.
        refreshAtsBubbles(0);
        refreshRollGate(engine, table);
        break;
      case 'RollStarted':
        // Both modes set rolling=true so apply() defers UI updates (puck
        // setOn/Off, refreshLegality, BankrollChanged → bankroll pill
        // update) into `pending`. The queue drains in runSettleFeedback
        // when the dice physically come to rest — that's what makes the
        // bankroll grow at the moment the dice land, not when the wire
        // frame arrives.
        rolling = true;
        // A new throw supersedes any bust check still counting down from the
        // previous roll — otherwise it pops while these dice are mid-air.
        // The settle handler schedules a fresh one against the newer state.
        cancelBustCheck();
        if (ui.shimmerClock?.getConfig().pauseDuringRoll) ui.shimmerClock.setPaused(true);
        if (table.shimmerClock?.getConfig().pauseDuringRoll) table.shimmerClock.setPaused(true);
        stateAtRollStart = engine.getState();
        captureAtsSeen();
        table.setRollEnabled(false);
        // Spectator: kick off the dice throw immediately using the result
        // embedded in RollStarted. Producer skips this — the dice are
        // already in flight from the user's tap.
        if (mode === 'spectate' && event.roll) {
          spectatorAutoThrew = true;
          spectatorDiceSettled = false;
          table.throwAndSettle(event.roll.d1, event.roll.d2, {
            onSettle: () => {
              spectatorDiceSettled = true;
              if (spectatorPendingSettle) {
                const cb = spectatorPendingSettle;
                spectatorPendingSettle = null;
                cb();
              }
            },
          });
          // Safety net: if the spectator's dice physics never reaches the
          // settle state (tab backgrounded → Phaser rAF paused; physics
          // edge case → bodies never sleep), force-drain pending after 5s
          // so the puck/bankroll/legality don't get stuck on pre-roll state.
          // The check is idempotent — if dice settled normally,
          // spectatorPendingSettle is already null and pending is already
          // empty, so the timeout does nothing.
          setTimeout(() => {
            if (!rolling) return;   // already drained normally
            if (spectatorPendingSettle) {
              const cb = spectatorPendingSettle;
              spectatorPendingSettle = null;
              cb();
            } else {
              // RollResolved hasn't even arrived; force-drain anyway so
              // queued UI updates don't accumulate indefinitely.
              for (const fn of pending) fn();
              pending.length = 0;
              rolling = false;
            }
          }, 5000);
        }
        break;
      case 'RollResolved': {
        const preState = stateAtRollStart;
        const roll = event.roll;
        const runSettleFeedback = async () => {
          feel.flinch(table.dice1, 'whisper');
          feel.flinch(table.dice2, 'whisper');
          // Stickman call: floats above the dice and fades out. Use the
          // pre-roll phase/point so a 7 during POINT reads "SEVEN OUT" (not
          // just "SEVEN"), and a 3 on come-out reads "ACE-DEUCE · CRAPS".
          const callContext = preState
            ? { phase: preState.phase, ...(preState.point !== undefined ? { point: preState.point } : {}) }
            : undefined;
          flashRollLabel(ui, rollLabel(event.roll, callContext));
          // Flash winning zones first so the felt highlights line up with
          // where the fly-chips will spawn. Skip display-only zones (e.g.
          // come-N): they overlap an interactive zone (place-N) covering the
          // same number box, so flashing both stacks two rectangles on the
          // same area — the place-N flash already covers the full box.
          if (preState) {
            for (const zone of table.zones) {
              const def = zone.def;
              if (def.interactive === false) continue;
              if (
                previewWouldWin(
                  {
                    type: def.type,
                    ...(def.number !== undefined ? { number: def.number as 4|5|6|8|9|10 } : {}),
                    ...(def.hopFaces !== undefined ? { hopFaces: def.hopFaces as [1|2|3|4|5|6, 1|2|3|4|5|6] } : {}),
                  },
                  roll,
                  preState,
                  rules,
                )
              ) {
                zone.flashWin();
              }
            }
          }

          // For each winning bet, spawn winnings chips left of the bet's
          // chip stack and fly them to the bankroll pill. The pill update
          // (queued in `pending` from the BankrollChanged → apply path) is
          // held until the chips arrive.
          const dpr = (table.game.registry.get('dpr') as number | undefined) ?? 1;
          const chipRadius = Math.round(14 * dpr);
          const bankrollAnchor = ui.getBankrollAnchor();
          const flyPromises: Promise<void>[] = [];
          if (preState) {
            for (const res of event.resolutions) {
              if (res.outcome !== 'win' || res.payout <= 0) continue;
              const bet = preState.bets.find((b) => b.id === res.betId);
              if (!bet) continue;
              // Use findZoneAnchorForBet so odds bets spawn from the parent
              // line/come zone's odds-chip-stack anchor (no `odds` zone exists
              // on its own). Without this, pass+odds wins on PointMade only
              // animated the line bet's payout — the (often larger) odds
              // payout silently went straight to the bankroll.
              const target = findZoneAnchorForBet(bet, preState, table);
              if (!target) continue;
              flyPromises.push(flyWinningChips({
                scene: ui,
                from: { x: target.anchor.x - chipRadius * 2, y: target.anchor.y },
                to: bankrollAnchor,
                cents: res.payout,
                chipRadius,
                dpr,
                burst: true,
              }));
            }
          }
          // Player-outcome shake: aggregate net delta + max multiplier across
          // resolved bets, then dispatch the right preset (celebrate / crash /
          // thump / no-op) via feel.fromResolution.
          if (preState && event.resolutions.length > 0) {
            let netDelta = 0;
            let maxMultiplier = 0;
            for (const res of event.resolutions) {
              if (res.outcome === 'continue') continue;
              const bet = preState.bets.find((b) => b.id === res.betId);
              if (!bet) continue;
              if (res.outcome === 'win') {
                netDelta += res.payout;
                const mult = bet.amount > 0 ? res.payout / bet.amount : 0;
                if (mult > maxMultiplier) maxMultiplier = mult;
              } else if (res.outcome === 'lose') {
                netDelta -= bet.amount;
              }
            }
            const sevenOut =
              preState.phase === 'POINT' && event.roll.total === 7;
            const pointMade =
              preState.phase === 'POINT'
              && preState.point !== undefined
              && event.roll.total === preState.point;
            const isRoundEnd = sevenOut || pointMade;
            feel.fromResolution(table, { netDelta, maxMultiplier, isRoundEnd });
          }
          // Loss feedback on a 7: pulse the border of every standard zone
          // whose bet just lost. Standalone-collection bets (ATS) already
          // get their own red-flash treatment in refreshAtsBubbles below.
          if (preState && event.roll.total === 7) {
            for (const res of event.resolutions) {
              if (res.outcome !== 'lose') continue;
              const bet = preState.bets.find((b) => b.id === res.betId);
              if (!bet) continue;
              const zone = findZoneForBet(bet, preState, table);
              zone?.flashRejected();
            }
          }
          // Slide any come bets that just picked up a number from the COME
          // box up to the matching number-box sub-position. Runs in parallel
          // with the win-fly so the dealer "moves the come" and "pays the
          // winners" beats happen on the same tick.
          const slidePromise = preState
            ? slideTraveledComeBets(preState, engine.getState(), table, dpr)
            : Promise.resolve();
          await Promise.all([...flyPromises, slidePromise]);

          // Now flush the deferred pill/rack updates so the bankroll
          // increments at the moment the chips land.
          for (const fn of pending) fn();
          pending.length = 0;
          rolling = false;
          ui.shimmerClock?.setPaused(false);
          table.shimmerClock?.setPaused(false);
          ui.setHistory(engine.getState().history);
          refreshChipStacks(engine, table);
          refreshAtsBubbles(event.roll.total);
          refreshRollGate(engine, table);
          refreshLegality(engine, table);
        };
        if (spectatorAutoThrew) {
          // Spectator's dice already auto-threw on RollStarted. Calling
          // throwAndSettle again would double-throw if they've returned
          // to home in the gap. Defer runSettleFeedback until the dice
          // physically settle so the stickman call + winning-zone flash
          // sync with the dice landing — not the WS frame arrival.
          spectatorAutoThrew = false;
          if (spectatorDiceSettled) {
            void runSettleFeedback();
          } else {
            spectatorPendingSettle = () => { void runSettleFeedback(); };
          }
        } else {
          table.throwAndSettle(event.roll.d1, event.roll.d2, {
            onSettle: () => {
              scheduleBustCheck();
              void runSettleFeedback();
            },
          });
        }
        break;
      }
    }
  }));

  // Cover the case where the player navigates to craps already broke
  // — no engine event fires on subscribe, so the post-switch check
  // above never runs unless they place a bet first.
  if (mode === 'play') {
    maybeShowBustModal(engine.getState());
  }

  // UI → Engine. In spectate mode the player has no inputs of their own —
  // every state change comes from the producer's wire events. Skip every
  // input registration below so taps/long-presses on the spectator's felt
  // are silently no-ops (the zones still exist for rendering, just inert).
  if (mode === 'play') {
  const onZoneClick = (def: ZoneDef) => {
    const denom = table.rack?.selectedCents ?? 500;
    // "Back up your bets" — when the player taps a line zone whose parent
    // bet is already on the layout (and the table is in a phase where odds
    // are legal), route the chip to an `odds`/`dontOdds` placement instead
    // of trying to add a fresh line bet (which would be rejected anyway).
    //
    // Pass / Don't Pass: parent must exist AND the table is on a point.
    // Place number boxes (place-N): if a Come bet has travelled to N, the
    //   tap backs up the come bet with odds; otherwise the existing
    //   place-bet path runs. Same for don't-come on the don't-side.
    const oddsTarget = pickOddsParent(engine.getState(), def);
    if (oddsTarget) {
      const oddsAmount = clampBetAmount(
        engine.getState(),
        rules,
        oddsTarget.type,
        denom,
        {
          parentBetId: oddsTarget.parent.id,
          ...(oddsTarget.number !== undefined ? { number: oddsTarget.number } : {}),
        },
      );
      const result = engine.placeBet(oddsTarget.type, oddsAmount, {
        parentBetId: oddsTarget.parent.id,
        ...(oddsTarget.number !== undefined ? { number: oddsTarget.number } : {}),
      });
      if (result instanceof Error) {
        if (result.message === 'insufficient bankroll') {
          table.rack.flashCannotAfford();
          feel.flinch(table.rack, 'whisper');
        } else if (
          result.message.startsWith('amount above odds cap')
          || result.message.startsWith('amount above table max')
        ) {
          const zone = table.zones.find((z) => z.def.id === def.id);
          zone?.flashRejected();
        }
        // eslint-disable-next-line no-console
        console.warn('placeBet (odds) rejected:', result.message);
      } else {
        feel.shake(table, 'whisper');
      }
      return;
    }

    // Come-N / DontCome-N taps with no come bet at N to back up fall through
    // to a plain place bet on the same number. The come-N hit area covers
    // only the top half of the number box; without this fallthrough, that
    // half would silently no-op on a number that has no come bet riding it.
    const isNumberedComeBox =
      (def.type === 'come' || def.type === 'dontCome') && def.number !== undefined;
    const placeType = isNumberedComeBox ? 'place' as const : def.type;
    const placeAmount = clampBetAmount(engine.getState(), rules, placeType, denom, {
      ...(def.number !== undefined ? { number: def.number as 4|5|6|8|9|10 } : {}),
      ...(def.hopFaces !== undefined ? { hopFaces: def.hopFaces as [1|2|3|4|5|6, 1|2|3|4|5|6] } : {}),
    });
    const result = engine.placeBet(placeType, placeAmount, {
      ...(def.number !== undefined ? { number: def.number as 4|5|6|8|9|10 } : {}),
      ...(def.hopFaces !== undefined ? { hopFaces: def.hopFaces as [1|2|3|4|5|6, 1|2|3|4|5|6] } : {}),
    });
    if (result instanceof Error) {
      if (result.message === 'insufficient bankroll') {
        table.rack.flashCannotAfford();
        feel.flinch(table.rack, 'whisper');
      } else if (result.message.startsWith('amount above table max')) {
        // The clicked zone is at its cap — flash its border red so the
        // rejection isn't silent. Match by id since (type, number) uniquely
        // identifies the canonical zone the user pressed.
        const zone = table.zones.find((z) => z.def.id === def.id);
        zone?.flashRejected();
      }
      // Surface as toast — for MVP, log to console.
      // eslint-disable-next-line no-console
      console.warn('placeBet rejected:', result.message);
    } else {
      feel.shake(table, 'whisper');
    }
  };
  table.events.on('zone:click', onZoneClick);
  disposers.push(() => table.events.off('zone:click', onZoneClick));

  const onZoneExplain = (def: ZoneDef) => {
    const help = resolveZoneHelp(def, engine.getState());
    if (help === null) return;
    ui.openHelp(help.title, help.body);
  };
  table.events.on('zone:explain', onZoneExplain);
  disposers.push(() => table.events.off('zone:explain', onZoneExplain));

  const onZoneLongPress = (def: ZoneDef) => {
    const state = engine.getState();
    const denom = table.rack?.selectedCents;

    // Peel odds first if any are riding this zone — symmetric with the click
    // handler, which routes to odds via pickOddsParent. This also dodges the
    // "animation fires but bet stays" trap on pass/dontPass during POINT
    // (parent line bet is non-removable, but odds always are).
    const oddsTarget = pickOddsParent(state, def);
    if (oddsTarget) {
      const oddsBets = state.bets.filter((b) => b.parentBetId === oddsTarget.parent.id);
      if (oddsBets.length > 0) {
        let target: typeof oddsBets[number] | undefined;
        if (denom !== undefined) {
          for (let i = oddsBets.length - 1; i >= 0; i--) {
            if (oddsBets[i]!.amount === denom) {
              target = oddsBets[i];
              break;
            }
          }
        }
        target ??= oddsBets[oddsBets.length - 1];
        if (target) {
          flyChipForRemoval(target);
          suppressNextBankrollPop = true;
          engine.removeBet(target.id);
          return;
        }
      }
    }

    // Fall through: remove a chip of the parent bet on this zone. The picker
    // narrows by the zone's identity — the COME zone targets only unsettled
    // come bets, come-N zones target only bets at that N — so a long-press on
    // unsettled COME after the unsettled chips run out becomes a no-op
    // instead of reaching into already-travelled come-at-N bets (and
    // cascading their odds via the engine's parent-cascade).
    const target = pickLongPressTarget(engine.getState(), def, denom);
    if (target) {
      flyChipForRemoval(target);
      suppressNextBankrollPop = true;
      engine.removeBet(target.id);
    }
  };
  table.events.on('zone:longPress', onZoneLongPress);
  disposers.push(() => table.events.off('zone:longPress', onZoneLongPress));

  const onRollRequest = () => engine.roll();
  table.events.on('roll:request', onRollRequest);
  disposers.push(() => table.events.off('roll:request', onRollRequest));

  const onResetClick = () => {
    // End the current recording session cleanly before resetting local state.
    // This freezes the current session_id in D1 (the grace alarm will mark
    // ended_at) so its share URL becomes a permanent replay of the game up
    // to this point — shareable after the fact, not just live. On the
    // post-reload re-entry, a fresh session_id is minted for the new game.
    void opts.recorder?.endSession();
    import('../persistence.js').then(({ reset }) => {
      reset();
      location.reload();
    });
  };
  opts.ui.events.on('reset:click', onResetClick);
  disposers.push(() => opts.ui.events.off('reset:click', onResetClick));

  const onTakeDownClick = () => {
    for (const bet of [...engine.getState().bets]) {
      flyChipForRemoval(bet);
      suppressNextBankrollPop = true;
      engine.removeBet(bet.id);
    }
  };

  // Spawn a fly-out chip from the bet's zone anchor → bankroll pill.
  // Captured BEFORE engine.removeBet so the source position reflects the
  // pre-removal stack location (the BetRemoved listener clears it after).
  const flyChipForRemoval = (bet: { type: string; number?: number; amount: number; parentBetId?: string }) => {
    const dprFly = (table.game.registry.get('dpr') as number | undefined) ?? 1;
    const target = findZoneAnchorForBet(bet, engine.getState(), table);
    if (!target) return;
    flyChipToRack({
      scene: ui,
      rackAnchor: table.rack.getChipAnchor(bet.amount),
      tableAnchor: target.anchor,
      cents: bet.amount,
      chipRadius: target.chipRadius,
      dpr: dprFly,
    });
  };
  opts.ui.events.on('takeDown:click', onTakeDownClick);
  disposers.push(() => opts.ui.events.off('takeDown:click', onTakeDownClick));

  // The broadcast slider is a fixed HTML element mounted from main.ts
  // (packages/game/src/components/broadcastSlider.ts). It talks directly to
  // recorder.startStreaming()/stopStreaming()/shareUrl() and handles its own
  // fallbacks for insecure contexts.

  // Persist the rack's selected denomination whenever the user picks a
  // different chip. Bankroll + tier unlocks flow through checkUnlocks
  // in the BankrollChanged handler above; rules aren't user-customizable, so
  // selectedDenom is the only thing this helper needs to track.
  const persistSelectedDenom = () => {
    save(setSelectedDenom(load(), 'craps', tableKey, opts.table.rack.selectedCents));
  };
  opts.table.events.on('rack:select', persistSelectedDenom);
  disposers.push(() => opts.table.events.off('rack:select', persistSelectedDenom));

  const gameEvents = opts.table.game.events;

  // Guard against a double-tap on Floor (or a tap while the confirm modal is
  // up) re-entering the leave flow and emitting navigate:lobby twice.
  let leaving = false;

  const doLeave = () => {
    if (leaving) return;
    leaving = true;
    // Cash out everything removeBet will refund (odds, place, come, field,
    // props, ATS, come-out line bets). Contract pass/dontPass flats on a point
    // no-op and remain.
    for (const bet of [...engine.getState().bets]) {
      engine.removeBet(bet.id);
    }
    // Forfeit whatever is left (the contract flats) — no refund.
    for (const bet of [...engine.getState().bets]) {
      engine.forfeitBet(bet.id);
    }
    // Reconcile the floor wallet from final cash-in-hand and clear the active
    // game. The turn recorder simply stops receiving inputs when the scene's
    // subscribeInputs disposer runs on shutdown (navigate:lobby); the current
    // turn is left open (only a seven-out closes a turn).
    save(endActiveGame(load(), engine.getState().bankroll));
    gameEvents.emit('navigate:lobby');
  };

  const onLeaveRequest = () => {
    const { forfeitCents } = computeForfeit(engine.getState());
    if (forfeitCents > 0) {
      showLeaveConfirmModal({
        forfeitCents,
        onConfirm: doLeave,
        onCancel: () => { /* stay at the table */ },
      });
      return;
    }
    doLeave();
  };
  gameEvents.on('leave:request', onLeaveRequest);
  disposers.push(() => gameEvents.off('leave:request', onLeaveRequest));
  } // end if (mode === 'play')

  if (mode === 'spectate') {
    const gameEvents = opts.table.game.events;
    const onSpectatorLeave = () => gameEvents.emit('navigate:lobby');
    gameEvents.on('leave:request', onSpectatorLeave);
    disposers.push(() => gameEvents.off('leave:request', onSpectatorLeave));
  }

  // Tear down on scene shutdown so a re-entry into the table starts with a
  // single bridge instance — no stacked engine subscribers, no stale UI
  // handlers driving multiple engines off the same dice roll.
  opts.table.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
    for (const dispose of disposers) dispose();
  });

  return engine;
}

/**
 * Resolve the standard-table BetZone for a bet — odds bets fall back to their
 * parent line zone (no separate `odds` zone exists). ATS bets live in their
 * own collection and are not returned here; callers that need them check
 * `table.atsZones` directly.
 */
function findZoneForBet(
  bet: { type: string; number?: number; parentBetId?: string },
  state: TableState,
  table: TableScene,
): BetZone | undefined {
  if ((bet.type === 'odds' || bet.type === 'dontOdds') && bet.parentBetId) {
    const parent = state.bets.find((b) => b.id === bet.parentBetId);
    if (!parent) return undefined;
    return table.zones.find(
      (zone) =>
        zone.def.type === parent.type &&
        (parent.number === undefined ? zone.def.number === undefined : zone.def.number === parent.number),
    );
  }
  return table.zones.find(
    (zone) =>
      zone.def.type === bet.type &&
      (bet.number === undefined ? zone.def.number === undefined : zone.def.number === bet.number),
  );
}

/**
 * Resolve the on-felt chip-stack anchor for a bet — handles odds chips
 * that visually aggregate onto their parent's zone, and ATS bets that live
 * in their own collection. Returns undefined for esoteric props with no
 * matching felt zone.
 */
function findZoneAnchorForBet(
  bet: { type: string; number?: number; parentBetId?: string },
  state: TableState,
  table: TableScene,
): { anchor: { x: number; y: number }; chipRadius: number } | undefined {
  if (bet.type === 'small' || bet.type === 'tall' || bet.type === 'all') {
    const z = table.atsZones.find((zone) => zone.def.type === bet.type);
    return z ? { anchor: z.getChipStackAnchor(), chipRadius: z.getChipRadius() } : undefined;
  }
  const z = findZoneForBet(bet, state, table);
  if (!z) return undefined;
  if (bet.type === 'odds' || bet.type === 'dontOdds') {
    return { anchor: z.getOddsChipStackAnchor(), chipRadius: z.getChipRadius() };
  }
  return { anchor: z.getChipStackAnchor(), chipRadius: z.getChipRadius() };
}

function refreshChipStacks(engine: Engine, table: TableScene) {
  const state = engine.getState();
  const byId = new Map(state.bets.map((b) => [b.id, b] as const));
  const parentTotals = new Map<string, number>();
  const oddsTotals = new Map<string, number>();
  for (const b of state.bets) {
    // Odds bets aggregate onto their parent's zone key — pass + odds reads as
    // two stacks on the same zone, with the odds stack offset (rendered by
    // BetZone). dontPass / come / dontCome follow the same shape.
    if ((b.type === 'odds' || b.type === 'dontOdds') && b.parentBetId) {
      const parent = byId.get(b.parentBetId);
      if (!parent) continue;
      let key: string;
      if (parent.type === 'pass' || parent.type === 'dontPass') {
        key = parent.type;
      } else if (parent.type === 'come' || parent.type === 'dontCome') {
        const n = parent.number;
        if (n === undefined) continue;
        key = `${parent.type}:${n}`;
      } else {
        continue;
      }
      oddsTotals.set(key, (oddsTotals.get(key) ?? 0) + b.amount);
      continue;
    }
    const key = `${b.type}${b.number !== undefined ? `:${b.number}` : ''}`;
    parentTotals.set(key, (parentTotals.get(key) ?? 0) + b.amount);
  }
  for (const zone of table.zones) {
    const def = zone.def;
    const key = `${def.type}${def.number !== undefined ? `:${def.number}` : ''}`;
    zone.setChipStackCents(parentTotals.get(key) ?? 0);
    zone.setOddsChipStackCents(oddsTotals.get(key) ?? 0);
  }
}

/**
 * If `def` represents a tap that should "back up" an existing line bet with
 * odds (rather than place a new line bet), return the matching parent.
 *
 *  - Tap pass / don't-pass during POINT, with that line bet on the layout
 *    → odds on the line.
 *  - Tap a come-N / dontCome-N number box with a come bet that has travelled
 *    to N → odds on the come bet. The come-N hit area covers only the TOP
 *    HALF of the number box (per its SVG marker), so the bottom half still
 *    falls through to plain place-bet placement on the same number.
 */
function pickOddsParent(
  state: TableState,
  def: ZoneDef,
):
  | { type: 'odds' | 'dontOdds'; parent: TableState['bets'][number]; number?: 4|5|6|8|9|10 }
  | undefined {
  if (def.type === 'pass' || def.type === 'dontPass') {
    if (state.phase !== 'POINT' || state.point === undefined) return undefined;
    const parent = state.bets.find((b) => b.type === def.type);
    if (!parent) return undefined;
    return {
      type: def.type === 'pass' ? 'odds' : 'dontOdds',
      parent,
      number: state.point as 4|5|6|8|9|10,
    };
  }
  if ((def.type === 'come' || def.type === 'dontCome') && def.number !== undefined) {
    const parent = state.bets.find((b) => b.type === def.type && b.number === def.number);
    if (!parent) return undefined;
    return {
      type: def.type === 'come' ? 'odds' : 'dontOdds',
      parent,
      number: def.number as 4|5|6|8|9|10,
    };
  }
  return undefined;
}


/**
 * Animate any come bets that just picked up a number sliding from the COME
 * zone up to the matching number-box sub-position (the come-N display zone).
 * All come bets numbered on the same roll travel to the same number, so a
 * single transient stack carrying the summed amount is enough.
 *
 * The source COME chip stack is hidden for the duration so the slide doesn't
 * double-render against the live stack. The post-roll refreshChipStacks call
 * re-renders both the COME (now empty) and come-N (now holding the moved
 * amount) at the end of the handler.
 */
function slideTraveledComeBets(
  preState: TableState,
  curState: TableState,
  table: TableScene,
  dpr: number,
): Promise<void> {
  let totalCents = 0;
  let toNumber: number | undefined;
  const preById = new Map(preState.bets.map((b) => [b.id, b]));
  for (const cur of curState.bets) {
    if (cur.type !== 'come' || cur.number === undefined) continue;
    const pre = preById.get(cur.id);
    if (pre && pre.type === 'come' && pre.number === undefined) {
      totalCents += cur.amount;
      toNumber = cur.number;
    }
  }
  if (totalCents === 0 || toNumber === undefined) return Promise.resolve();

  const fromZone = table.zones.find((z) => z.def.id === 'come');
  const toZone = table.zones.find((z) => z.def.id === `come-${toNumber}`);
  if (!fromZone || !toZone) return Promise.resolve();

  const fromAnchor = fromZone.getChipStackAnchor();
  const toAnchor = toZone.getChipStackAnchor();
  // Hide the source so the live stack and the transient don't visually overlap.
  // refreshChipStacks at the end of the roll handler will repaint both zones
  // from current state (COME → 0, come-N → totalCents).
  fromZone.setChipStackCents(0);

  // Match the destination zone's chip radius, NOT the dpr-derived fly-chip
  // radius — those differ by ~3× on a typical phone, which made the slide
  // chip visually overshoot down into the place-bet position even though
  // its anchor was correctly at the come-N spot.
  const transient = new ChipStackVisual({
    scene: table,
    anchor: fromAnchor,
    chipRadius: toZone.getChipRadius(),
    dpr,
    depth: 200,
  });
  transient.setCents(totalCents);
  // Post-slide come-N total (sum of all come bets at this number) — set the
  // destination's chip stack to this BEFORE destroying the transient so the
  // chips never visually disappear: the transient sits on top of an
  // already-painted come-N stack at the moment it's torn down. Without this,
  // there's a 1-2 frame gap between transient.destroy() and the eventual
  // refreshChipStacks call where the chips flash invisible.
  const destTotal = curState.bets
    .filter((b) => b.type === 'come' && b.number === toNumber)
    .reduce((sum, b) => sum + b.amount, 0);
  return transient.tweenTo(toAnchor.x, toAnchor.y, 400).then(() => {
    toZone.setChipStackCents(destTotal);
    transient.destroy();
  });
}

/**
 * Debug-mode dice that always rolls a fixed total (split across two dice
 * deterministically — e.g. force=8 → d1=4, d2=4). Trigger via:
 *   ?force=8       — query string on the URL
 *   localStorage.setItem('forceRoll', '8')
 *
 * For totals that don't have a hardway split (3, 5, 7, 9, 11), uses the
 * smallest valid pair (e.g. 7 → 1+6).
 */
function pickDice(): import('./bridgeTypes.js').DiceSource {
  const force = readForceRoll();
  if (force === undefined) return new FairDice();
  return new ForcedTotalDice(force);
}

/** Pop the bust-recovery modal when the player's total position (rack +
 *  felt) is below the threshold. Position avoids the false-positive of
 *  "$0 cash but $1000 on the pass line" — those chips just haven't
 *  resolved yet. Safe to call on every engine event:
 *  - Once shown, a sessionStorage flag suppresses repeats within the
 *    same dip — a player who chose "Keep playing" doesn't get re-prompted
 *    on each losing roll.
 *  - When position recovers back above the threshold, the flag clears,
 *    so a future dip from a winning streak gets the modal again rather
 *    than the player silently busting a second time. */
function maybeShowBustModal(state: TableState): void {
  if (typeof sessionStorage === 'undefined' || typeof document === 'undefined') return;
  let wageredCents = 0;
  for (const bet of state.bets) wageredCents += bet.amount;
  const aboveThreshold = state.bankroll + wageredCents >= LOW_POSITION_THRESHOLD_CENTS;
  const suppressed = sessionStorage.getItem(BUST_MODAL_SESSION_KEY) === '1';
  if (aboveThreshold) {
    // Re-arm: player recovered, so the next bust is a fresh event worth flagging.
    if (suppressed) sessionStorage.removeItem(BUST_MODAL_SESSION_KEY);
    return;
  }
  if (suppressed) return;
  sessionStorage.setItem(BUST_MODAL_SESSION_KEY, '1');
  openBustModal();
}

function readForceRoll(): number | undefined {
  try {
    const params = new URLSearchParams(location.search);
    const fromUrl = params.get('force');
    if (fromUrl !== null) {
      const n = Number(fromUrl);
      if (n >= 2 && n <= 12) return n;
    }
    const fromStorage = localStorage.getItem('forceRoll');
    if (fromStorage !== null) {
      const n = Number(fromStorage);
      if (n >= 2 && n <= 12) return n;
    }
  } catch {
    // Storage access can throw in private mode — silently fall through.
  }
  return undefined;
}

class ForcedTotalDice {
  constructor(private total: number) {}
  roll(): { d1: 1 | 2 | 3 | 4 | 5 | 6; d2: 1 | 2 | 3 | 4 | 5 | 6 } {
    const t = this.total;
    // Pair (a, b) where a+b=t, both in [1,6], preferring hardways (a==b)
    // when the total is even — feels intentional in the visual.
    if (t >= 2 && t <= 12) {
      if (t % 2 === 0 && t / 2 >= 1 && t / 2 <= 6) {
        const half = t / 2 as 1 | 2 | 3 | 4 | 5 | 6;
        return { d1: half, d2: half };
      }
      // Odd or out-of-hardway range: pick smallest valid pair.
      const a = Math.max(1, t - 6) as 1 | 2 | 3 | 4 | 5 | 6;
      const b = (t - a) as 1 | 2 | 3 | 4 | 5 | 6;
      return { d1: a, d2: b };
    }
    // Fall back to a fair-style 4-4 if asked for an invalid total.
    return { d1: 4, d2: 4 };
  }
}

function refreshRollGate(engine: Engine, table: TableScene) {
  // Real craps requires the shooter to have a line bet (Pass or Don't Pass)
  // up before rolling. Mirror that here.
  const state = engine.getState();
  const hasLineBet = state.bets.some(
    (b) => b.type === 'pass' || b.type === 'dontPass',
  );
  table.setRollEnabled(hasLineBet);

  // Breathing gold zone wash on pass / don't-pass when a line bet is the
  // expected next action (come-out, no line bet yet). Stops the moment a line
  // bet lands or the table moves to POINT.
  const cueLineBet = !hasLineBet && state.phase === 'COME_OUT';
  for (const zone of table.zones) {
    if (zone.def.type !== 'pass' && zone.def.type !== 'dontPass') continue;
    if (cueLineBet) zone.startBreathingGlow();
    else zone.stopBreathingGlow();
  }
}

function refreshLegality(engine: Engine, table: TableScene) {
  const state = engine.getState();
  for (const zone of table.zones) {
    const def = zone.def;
    const r = engine.canPlaceBet(def.type, {
      ...(def.number !== undefined ? { number: def.number as 4|5|6|8|9|10 } : {}),
      ...(def.hopFaces !== undefined ? { hopFaces: def.hopFaces as [1|2|3|4|5|6, 1|2|3|4|5|6] } : {}),
    });
    if (r.allowed) {
      zone.setLegality(true);
      continue;
    }
    // Pass / Don't Pass / Place-N can still be tapped to back up an existing
    // line bet with odds — surface the zone as legal in that case so it
    // doesn't dim during POINT phase even though the canonical bet would be
    // rejected.
    const oddsTarget = pickOddsParent(state, def);
    if (oddsTarget) {
      zone.setLegality(true);
    } else {
      zone.setLegality(false, r.reason);
    }
  }
}

